import { Button, cn, Dialog } from "@scribenote/brian-design-system";
import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type SyntheticEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { SourceSize } from "./fixtureBoxes";
import { ago } from "./words";

/** Pointer travel, in CSS pixels, past which a press is a pan rather than a click. */
const DRAG_SLOP = 4;

interface Point {
  x: number;
  y: number;
}

interface Drag {
  start: Point;
  scroll: Point;
  moved: boolean;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Where [clientX, clientY] falls on an `object-contain` image, as fractions of the picture. */
const fractionOnPicture = (
  img: HTMLImageElement,
  size: SourceSize,
  clientX: number,
  clientY: number,
): Point => {
  const box = img.getBoundingClientRect();
  const scale = Math.min(box.width / size.width, box.height / size.height);
  const w = size.width * scale;
  const h = size.height * scale;
  return {
    x: clamp01((clientX - box.left - (box.width - w) / 2) / w),
    y: clamp01((clientY - box.top - (box.height - h) / 2) / h),
  };
};

/**
 * A room's newest still at its captured resolution: fitted to the dialog, and
 * at one image pixel per screen pixel on a click, centred where it was clicked
 * and panned by dragging. It follows the feed, which swaps in a new still only
 * once it has loaded, so an update never blanks it and the view stays put.
 * Sizes come from the decoded image rather than the still's metadata, so a
 * device that reports one size and encodes another cannot stretch it.
 */
export const StillViewer = ({
  roomName,
  url,
  size,
  capturedAt,
  now,
  onClose,
}: {
  roomName: string;
  url: string;
  size: SourceSize;
  capturedAt: string;
  now: number;
  onClose: () => void;
}) => {
  const [actualSize, setActualSize] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [decoded, setDecoded] = useState<SourceSize | null>(null);
  const px = decoded ?? size;
  const viewport = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const focus = useRef<Point>({ x: 0.5, y: 0.5 });
  const drag = useRef<Drag | null>(null);

  // The focus is a fraction of the picture, so a still at a new resolution
  // keeps showing the same spot rather than the same pixel offsets.
  useLayoutEffect(() => {
    const el = viewport.current;
    if (!actualSize || !el) return;
    el.scrollLeft = focus.current.x * px.width - el.clientWidth / 2;
    el.scrollTop = focus.current.y * px.height - el.clientHeight / 2;
  }, [actualSize, px.width, px.height]);

  const onScroll = () => {
    const el = viewport.current;
    if (!actualSize || !el) return;
    focus.current = {
      x: clamp01((el.scrollLeft + el.clientWidth / 2) / px.width),
      y: clamp01((el.scrollTop + el.clientHeight / 2) / px.height),
    };
  };

  const onImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
    if (width > 0 && height > 0 && (width !== px.width || height !== px.height))
      setDecoded({ width, height });
  };

  const toggle = (at: Point | null) => {
    if (!actualSize) focus.current = at ?? { x: 0.5, y: 0.5 };
    setActualSize((v) => !v);
  };

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const panned = drag.current?.moved;
    drag.current = null;
    if (panned) return;
    const img = image.current;
    toggle(
      img && !actualSize
        ? fractionOnPicture(img, px, e.clientX, e.clientY)
        : null,
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    toggle(null);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const el = viewport.current;
    if (!actualSize || !el || e.button !== 0) return;
    // Captured, so a pan that runs past the viewport edge keeps going.
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      start: { x: e.clientX, y: e.clientY },
      scroll: { x: el.scrollLeft, y: el.scrollTop },
      moved: false,
    };
    setDragging(true);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const el = viewport.current;
    const d = drag.current;
    if (!el || !d || !dragging) return;
    const dx = e.clientX - d.start.x;
    const dy = e.clientY - d.start.y;
    if (Math.abs(dx) + Math.abs(dy) > DRAG_SLOP) d.moved = true;
    el.scrollLeft = d.scroll.x - dx;
    el.scrollTop = d.scroll.y - dy;
  };

  const endDrag = () => setDragging(false);

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${roomName} · ${px.width}×${px.height} · ${ago(capturedAt, now)}`}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div
        ref={viewport}
        role="button"
        tabIndex={0}
        aria-label={actualSize ? "Fit to window" : "Show actual size"}
        data-dd-privacy="hidden"
        onClick={onClick}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onScroll={onScroll}
        className={cn(
          "h-full w-full touch-none bg-recess select-none focus-visible:outline-2 focus-visible:outline-accent",
          actualSize
            ? cn("overflow-auto", dragging ? "cursor-grabbing" : "cursor-grab")
            : "flex cursor-zoom-in items-center justify-center",
        )}
      >
        <img
          ref={image}
          src={url}
          alt={`Still from ${roomName} at ${px.width}×${px.height}`}
          referrerPolicy="no-referrer"
          draggable={false}
          onLoad={onImageLoad}
          className={cn(
            "block",
            actualSize ? "max-w-none" : "h-full w-full object-contain",
          )}
          style={
            actualSize ? { width: px.width, height: px.height } : undefined
          }
        />
      </div>
    </Dialog>
  );
};
